import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar';
import SectionCheckbox from '../../components/SectionCheckbox';
import Quiz from '../../components/Quiz';
import { PromptBox } from '../../components/CopyButton';
import PromptPlayground from '../../components/PromptPlayground';
import TelegramWidget from '../../components/TelegramWidget';
import '../Dashboard.css';
import '../modules/Module.css';
import './Track.css';

const MODULE_ID = 1;

// Quiz questions
const quizQuestions = [
  {
    question: "Quelle est la différence principale entre GPT-4 et GPT-4o ?",
    options: [
      "GPT-4o est moins intelligent",
      "GPT-4o est multimodal (texte, image, audio) et plus rapide",
      "GPT-4 peut générer des images",
      "Il n'y a aucune différence"
    ],
    correctAnswer: 1,
    explanation: "GPT-4o (o pour 'omni') est multimodal : il peut traiter texte, images et audio. Il est aussi 2x plus rapide et moins cher que GPT-4."
  },
  {
    question: "Quel abonnement donne accès à GPT-4, DALL-E 3 et aux GPTs personnalisés ?",
    options: [
      "ChatGPT Free",
      "ChatGPT Plus (20$/mois)",
      "ChatGPT Team",
      "Aucun, tout est gratuit"
    ],
    correctAnswer: 1,
    explanation: "ChatGPT Plus à 20$/mois donne accès à GPT-4, GPT-4o, DALL-E 3, navigation web, analyse de données et création de GPTs."
  },
  {
    question: "Dans la méthode CRISP, que signifie le 'R' ?",
    options: [
      "Résultat",
      "Rôle",
      "Réponse",
      "Recherche"
    ],
    correctAnswer: 1,
    explanation: "R = Rôle. Demandez à ChatGPT d'incarner un expert spécifique (copywriter, avocat, coach...) pour des réponses plus pertinentes."
  },
  {
    question: "Quelle technique permet d'obtenir les meilleures réponses de ChatGPT ?",
    options: [
      "Poser des questions courtes",
      "Utiliser le tutoiement",
      "Donner du contexte détaillé et des exemples",
      "Répéter la même question plusieurs fois"
    ],
    correctAnswer: 2,
    explanation: "Plus vous donnez de contexte, d'exemples et de spécifications, plus la réponse sera précise et utile."
  },
  {
    question: "ChatGPT peut-il accéder à internet en temps réel ?",
    options: [
      "Non, jamais",
      "Oui, avec GPT-4 et la navigation web activée",
      "Seulement sur mobile",
      "Uniquement en anglais"
    ],
    correctAnswer: 1,
    explanation: "Avec GPT-4/GPT-4o et la navigation web activée, ChatGPT peut rechercher des informations actuelles sur internet."
  }
];

// Playground CRISP
const crispVariables = [
  {
    name: 'context',
    label: 'Contexte (qui êtes-vous, situation)',
    type: 'textarea',
    placeholder: 'Ex: Je suis entrepreneur, je lance une boutique e-commerce de vêtements éco-responsables...'
  },
  {
    name: 'role',
    label: 'Rôle (expert souhaité)',
    type: 'text',
    placeholder: 'Ex: expert en marketing digital, copywriter senior, consultant business...'
  },
  {
    name: 'instructions',
    label: 'Instructions (ce que vous voulez)',
    type: 'textarea',
    placeholder: 'Ex: Crée-moi une stratégie de lancement en 30 jours avec les actions clés...'
  },
  {
    name: 'specifications',
    label: 'Spécifications (format, longueur, ton)',
    type: 'text',
    placeholder: 'Ex: Format tableau, ton professionnel, inclure budget estimé'
  }
];

const crispTemplate = `**CONTEXTE :**
{{context}}

**TON RÔLE :**
Agis comme un {{role}}.

**INSTRUCTIONS :**
{{instructions}}

**SPÉCIFICATIONS :**
{{specifications}}

Commence maintenant.`;

const ChatGPTTrack = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { progress } = useProgress();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    intro: true,
    versions: false,
    installation: false,
    interface: false,
    crisp: false,
    usecases: false,
    advanced: false,
    work: false,
    projects: false,
    limits: false,
    fears: false,
    vsothers: false,
    troubleshoot: false,
    faq: false
  });

  if (!isAuthenticated) { navigate('/login'); return null; }

  const handleLogout = () => { logout(); navigate('/'); };
  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  const totalProgress = progress?.totalProgress || 0;

  return (
    <div className="dashboard-layout">
      {/* SIDEBAR PRO */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/dashboard')}>
          <span className="logo-text">F12IA</span>
          <span className="logo-tm">™</span>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">{user?.firstName?.charAt(0).toUpperCase() || 'U'}</div>
          <div className="user-info">
            <span className="user-name">{user?.firstName || 'Utilisateur'}</span>
            <span className="user-role">Membre</span>
          </div>
        </div>

        <nav className="sidebar-nav-pro">
          <div className="nav-label">MENU</div>
          <div className="nav-item" onClick={() => navigate('/dashboard')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
            <span className="nav-text">Tableau de bord</span>
          </div>

          <div className="nav-label">FORMATIONS</div>
          <div className="nav-item active" onClick={() => navigate('/formation/chatgpt')}>
            <span className="nav-icon chatgpt-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729z"/></svg></span>
            <span className="nav-text">ChatGPT</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/formation/claude')}>
            <span className="nav-icon claude-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M4.709 15.955l4.72-2.647.08-.23-.08-.128-2.251-3.614-.144-.072-4.794.893-.071.158.503 2.332.014.13 2.023 3.178z"/></svg></span>
            <span className="nav-text">Claude Code</span>
          </div>
          <div className="nav-item fusion-nav" onClick={() => navigate('/formation/fusion')}>
            <span className="nav-icon fusion-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg></span>
            <span className="nav-text">Fusion</span>
            <span className="nav-badge-pro">PRO</span>
          </div>

          <div className="nav-label">COMPTE</div>
          <div className="nav-item" onClick={() => navigate('/profile')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg></span>
            <span className="nav-text">Mon profil</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/resources')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></span>
            <span className="nav-text">Ressources</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/certificate')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M8 14l-2 8 6-3 6 3-2-8"/></svg></span>
            <span className="nav-text">Certificat</span>
          </div>
        </nav>

        <div className="sidebar-progress-pro">
          <div className="progress-header">
            <span className="progress-title">Progression</span>
            <span className="progress-value">{Math.round(totalProgress)}%</span>
          </div>
          <div className="progress-bar-pro">
            <div className="progress-fill-pro" style={{ width: `${totalProgress}%` }} />
          </div>
        </div>

        <div className="sidebar-bottom">
          <div className="nav-item logout-item" onClick={handleLogout}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span>
            <span className="nav-text">Déconnexion</span>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="module-header track-header chatgpt-track">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← Retour</button>
          <div className="track-badge chatgpt">FORMATION COMPLÈTE</div>
          <h1>🧠 Maîtriser ChatGPT de A à Z</h1>
          <p className="module-subtitle">Votre assistant IA quotidien : de débutant à expert en quelques heures</p>
          <ProgressBar moduleId={MODULE_ID} />
        </div>

        <div className="module-content">

          {/* ==================== SECTION 1: C'EST QUOI CHATGPT ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('intro')}>
              <div className="section-toggle-content">
                <span className="section-number">01</span>
                <div>
                  <h2>C'est quoi ChatGPT ?</h2>
                  <p>Comprendre l'outil qui révolutionne le monde</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.intro ? '▼' : '▶'}</span>
            </div>

            {expandedSections.intro && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Comprendre ChatGPT en 2 minutes</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="intro" />
                </div>

                {/* DÉFINITION ULTRA SIMPLE */}
                <div className="highlight-box gold">
                  <strong>🎯 C'est quoi ChatGPT ?</strong><br/><br/>
                  <span style={{fontSize: '1.1rem'}}>
                    ChatGPT, c'est comme <strong>envoyer un message à un ami super intelligent</strong> qui sait (presque) tout
                    et peut t'aider sur n'importe quel sujet.<br/><br/>
                    👉 Tu lui écris une question ou une demande en français<br/>
                    👉 Il te répond instantanément, comme dans une conversation WhatsApp
                  </span>
                </div>

                {/* EXEMPLE CONCRET */}
                <h4>💬 Exemple concret : comment ça marche ?</h4>
                <div className="conversation-example" style={{background: 'rgba(16, 185, 129, 0.1)', borderRadius: '16px', padding: '20px', marginBottom: '24px'}}>
                  <div style={{marginBottom: '16px'}}>
                    <div style={{background: '#1a1a2e', padding: '12px 16px', borderRadius: '12px', marginBottom: '8px', maxWidth: '80%'}}>
                      <strong style={{color: '#d4af37'}}>👤 Toi :</strong>
                      <p style={{margin: '8px 0 0 0', color: '#fff'}}>"Aide-moi à écrire un email pour demander une augmentation à mon patron"</p>
                    </div>
                    <div style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '12px 16px', borderRadius: '12px', marginLeft: '20%'}}>
                      <strong style={{color: '#fff'}}>🤖 ChatGPT :</strong>
                      <p style={{margin: '8px 0 0 0', color: '#fff', fontSize: '0.9rem'}}>
                        "Objet : Demande d'entretien concernant ma rémunération<br/><br/>
                        Bonjour [Nom du patron],<br/><br/>
                        Je me permets de solliciter un entretien pour discuter de ma rémunération.
                        Depuis [X mois/années], j'ai contribué à [exemples de réussites]...<br/><br/>
                        [Email complet et professionnel de 10 lignes]"
                      </p>
                    </div>
                  </div>
                  <p style={{textAlign: 'center', color: 'rgba(255,255,255,0.7)', margin: 0, fontSize: '0.9rem'}}>
                    ⚡ En 5 secondes, tu as un email professionnel prêt à envoyer !
                  </p>
                </div>

                {/* LE MOMENT AHA */}
                <div className="tip-box" style={{background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.05) 100%)', border: '2px solid #d4af37'}}>
                  <div className="tip-icon">🤯</div>
                  <div className="tip-content">
                    <h4>Le moment "Aha!" — Imagine ça :</h4>
                    <p>
                      Tu as accès à un <strong>expert en marketing</strong>, un <strong>rédacteur professionnel</strong>,
                      un <strong>traducteur</strong>, un <strong>conseiller business</strong>, un <strong>prof particulier</strong>,
                      et un <strong>assistant personnel</strong>...<br/><br/>
                      <strong>Tous disponibles 24h/24, 7j/7, gratuitement, dans une seule conversation.</strong><br/><br/>
                      C'est ça ChatGPT. 🚀
                    </p>
                  </div>
                </div>

                {/* CE QUE TU PEUX FAIRE */}
                <h4>✅ Tout ce que tu peux faire avec ChatGPT</h4>
                <div className="info-box">
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px'}}>
                    <div>
                      <p style={{fontWeight: 'bold', marginBottom: '8px', color: '#10b981'}}>📝 Écrire & Rédiger</p>
                      <ul style={{margin: 0, paddingLeft: '20px'}}>
                        <li>Emails professionnels</li>
                        <li>Posts réseaux sociaux</li>
                        <li>Lettres de motivation</li>
                        <li>Articles, scripts, textes</li>
                      </ul>
                    </div>
                    <div>
                      <p style={{fontWeight: 'bold', marginBottom: '8px', color: '#10b981'}}>💡 Réfléchir & Planifier</p>
                      <ul style={{margin: 0, paddingLeft: '20px'}}>
                        <li>Développer une idée de projet</li>
                        <li>Créer un business plan</li>
                        <li>Trouver des solutions</li>
                        <li>Organiser ses pensées</li>
                      </ul>
                    </div>
                    <div>
                      <p style={{fontWeight: 'bold', marginBottom: '8px', color: '#10b981'}}>🎨 Créer & Analyser</p>
                      <ul style={{margin: 0, paddingLeft: '20px'}}>
                        <li>Générer des images (DALL-E)</li>
                        <li>Analyser des documents</li>
                        <li>Traduire en 95+ langues</li>
                        <li>Résumer des textes longs</li>
                      </ul>
                    </div>
                    <div>
                      <p style={{fontWeight: 'bold', marginBottom: '8px', color: '#10b981'}}>🎓 Apprendre & Comprendre</p>
                      <ul style={{margin: 0, paddingLeft: '20px'}}>
                        <li>Expliquer des concepts</li>
                        <li>Préparer des examens</li>
                        <li>Apprendre une langue</li>
                        <li>Comprendre du code</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* CHATGPT VS CLAUDE CODE */}
                <div className="warning-box" style={{background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.05) 100%)', borderColor: '#8b5cf6'}}>
                  <strong>🔀 ChatGPT vs Claude Code — Quelle différence ?</strong>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '16px'}}>
                    <div style={{background: 'rgba(16, 185, 129, 0.1)', padding: '16px', borderRadius: '12px'}}>
                      <p style={{fontWeight: 'bold', color: '#10b981', margin: '0 0 8px 0'}}>🧠 ChatGPT = Le cerveau</p>
                      <ul style={{margin: 0, paddingLeft: '20px', fontSize: '0.9rem'}}>
                        <li>Réfléchir, planifier</li>
                        <li>Développer des idées</li>
                        <li>Rédiger du contenu</li>
                        <li>Analyser, résoudre</li>
                      </ul>
                    </div>
                    <div style={{background: 'rgba(139, 92, 246, 0.1)', padding: '16px', borderRadius: '12px'}}>
                      <p style={{fontWeight: 'bold', color: '#8b5cf6', margin: '0 0 8px 0'}}>🛠️ Claude Code = Les mains</p>
                      <ul style={{margin: 0, paddingLeft: '20px', fontSize: '0.9rem'}}>
                        <li>Créer des sites web</li>
                        <li>Développer des apps</li>
                        <li>Coder des projets</li>
                        <li>Automatiser des tâches</li>
                      </ul>
                    </div>
                  </div>
                  <p style={{marginTop: '16px', marginBottom: 0, textAlign: 'center', fontSize: '0.95rem'}}>
                    <strong>➡️ Utilise ChatGPT pour réfléchir, puis Claude Code pour créer !</strong>
                  </p>
                </div>

                {/* COMMENT ÇA MARCHE TECHNIQUEMENT - SIMPLIFIÉ */}
                <h4>🔧 Comment ça marche ? (version simple)</h4>
                <div className="info-box">
                  <p>ChatGPT a été créé par <strong>OpenAI</strong> (une entreprise américaine). Voici comment il fonctionne :</p>
                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px', marginTop: '12px'}}>
                    <p style={{margin: '0 0 12px 0'}}>
                      <strong>📚 Étape 1 :</strong> ChatGPT a "lu" des milliards de textes (livres, sites web, articles...)
                    </p>
                    <p style={{margin: '0 0 12px 0'}}>
                      <strong>🧠 Étape 2 :</strong> Il a appris les règles du langage, les connaissances, les styles d'écriture
                    </p>
                    <p style={{margin: 0}}>
                      <strong>💬 Étape 3 :</strong> Quand tu lui parles, il utilise tout ça pour te répondre intelligemment
                    </p>
                  </div>
                  <p style={{marginTop: '16px', marginBottom: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem'}}>
                    💡 <em>C'est comme un étudiant qui aurait lu toute la bibliothèque du monde et qui peut t'aider sur n'importe quoi !</em>
                  </p>
                </div>

                <h4>ChatGPT vs Google : Quelle différence ?</h4>
                <div className="comparison-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Google</th>
                        <th>ChatGPT</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Vous donne des liens vers des sites</td>
                        <td>Vous donne directement la réponse</td>
                      </tr>
                      <tr>
                        <td>Vous cherchez l'info dans les résultats</td>
                        <td>Il synthétise et explique pour vous</td>
                      </tr>
                      <tr>
                        <td>Répond à des mots-clés</td>
                        <td>Comprend des phrases complètes</td>
                      </tr>
                      <tr>
                        <td>Résultats figés</td>
                        <td>Réponses personnalisées selon votre demande</td>
                      </tr>
                      <tr>
                        <td>Ne peut pas créer de contenu</td>
                        <td>Crée du contenu sur mesure</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h4>Les chiffres impressionnants de ChatGPT</h4>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-number">100M+</div>
                    <div className="stat-label">Utilisateurs en 2 mois</div>
                    <div className="stat-detail">Record historique de croissance</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">1.8B</div>
                    <div className="stat-label">Visites mensuelles</div>
                    <div className="stat-detail">Top 10 mondial des sites</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">175B</div>
                    <div className="stat-label">Paramètres (GPT-3)</div>
                    <div className="stat-detail">GPT-4 : encore plus puissant</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">95+</div>
                    <div className="stat-label">Langues supportées</div>
                    <div className="stat-detail">Dont le français parfaitement</div>
                  </div>
                </div>

                <div className="tip-box">
                  <div className="tip-icon">🎯</div>
                  <div className="tip-content">
                    <h4>En résumé : ChatGPT c'est...</h4>
                    <p>
                      ✅ Un assistant qui répond à tes questions en quelques secondes<br/>
                      ✅ Un rédacteur qui écrit emails, posts, lettres à ta place<br/>
                      ✅ Un conseiller qui t'aide à développer tes idées<br/>
                      ✅ Un traducteur qui parle 95+ langues<br/>
                      ✅ Un prof qui explique tout simplement<br/><br/>
                      <strong>Gratuit, disponible 24/7, et il ne juge jamais tes questions ! 😄</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 2: LES VERSIONS ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('versions')}>
              <div className="section-toggle-content">
                <span className="section-number">02</span>
                <div>
                  <h2>Les Versions de ChatGPT</h2>
                  <p>GPT-3.5, GPT-4, GPT-4o, GPT-4 Turbo... Quelle différence ?</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.versions ? '▼' : '▶'}</span>
            </div>

            {expandedSections.versions && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Comprendre les modèles OpenAI</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="versions" />
                </div>

                <div className="version-cards">
                  <div className="version-card free">
                    <div className="version-header">
                      <h4>GPT-3.5</h4>
                      <span className="version-badge free">GRATUIT</span>
                    </div>
                    <div className="version-content">
                      <p className="version-desc">Le modèle de base, accessible à tous</p>
                      <ul>
                        <li>✅ Rapide et fluide</li>
                        <li>✅ Bon pour les tâches simples</li>
                        <li>✅ Illimité gratuitement</li>
                        <li>❌ Moins précis que GPT-4</li>
                        <li>❌ Raisonnement limité</li>
                        <li>❌ Pas d'images ni web</li>
                      </ul>
                      <div className="version-use">
                        <strong>Idéal pour :</strong> Questions simples, rédaction basique, brainstorming
                      </div>
                    </div>
                  </div>

                  <div className="version-card pro">
                    <div className="version-header">
                      <h4>GPT-4</h4>
                      <span className="version-badge pro">PLUS - 20$/mois</span>
                    </div>
                    <div className="version-content">
                      <p className="version-desc">Le modèle premium, beaucoup plus intelligent</p>
                      <ul>
                        <li>✅ Raisonnement avancé</li>
                        <li>✅ Meilleure compréhension du contexte</li>
                        <li>✅ Moins d'erreurs factuelles</li>
                        <li>✅ Analyse d'images</li>
                        <li>✅ Navigation web</li>
                        <li>❌ Plus lent que GPT-3.5</li>
                      </ul>
                      <div className="version-use">
                        <strong>Idéal pour :</strong> Tâches complexes, analyse, code, rédaction pro
                      </div>
                    </div>
                  </div>

                  <div className="version-card best">
                    <div className="version-header">
                      <h4>GPT-4o</h4>
                      <span className="version-badge best">RECOMMANDÉ</span>
                    </div>
                    <div className="version-content">
                      <p className="version-desc">Le meilleur rapport qualité/vitesse</p>
                      <ul>
                        <li>✅ Aussi intelligent que GPT-4</li>
                        <li>✅ 2x plus rapide</li>
                        <li>✅ Multimodal (texte + image + audio)</li>
                        <li>✅ Conversation vocale naturelle</li>
                        <li>✅ Moins cher via API</li>
                        <li>✅ Inclus dans Plus</li>
                      </ul>
                      <div className="version-use">
                        <strong>Idéal pour :</strong> Usage quotidien, toutes les tâches
                      </div>
                    </div>
                  </div>

                  <div className="version-card enterprise">
                    <div className="version-header">
                      <h4>GPT-4 Turbo</h4>
                      <span className="version-badge enterprise">API</span>
                    </div>
                    <div className="version-content">
                      <p className="version-desc">Optimisé pour les développeurs</p>
                      <ul>
                        <li>✅ Contexte 128K tokens</li>
                        <li>✅ Connaissances jusqu'à avril 2024</li>
                        <li>✅ JSON mode natif</li>
                        <li>✅ Appels de fonctions</li>
                        <li>✅ Vision intégrée</li>
                        <li>❌ Uniquement via API</li>
                      </ul>
                      <div className="version-use">
                        <strong>Idéal pour :</strong> Développeurs, applications, automatisation
                      </div>
                    </div>
                  </div>
                </div>

                <h4>Tableau comparatif complet</h4>
                <div className="comparison-table full">
                  <table>
                    <thead>
                      <tr>
                        <th>Fonctionnalité</th>
                        <th>GPT-3.5</th>
                        <th>GPT-4</th>
                        <th>GPT-4o</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Prix</td>
                        <td>Gratuit</td>
                        <td>20$/mois</td>
                        <td>20$/mois</td>
                      </tr>
                      <tr>
                        <td>Intelligence</td>
                        <td>⭐⭐⭐</td>
                        <td>⭐⭐⭐⭐⭐</td>
                        <td>⭐⭐⭐⭐⭐</td>
                      </tr>
                      <tr>
                        <td>Vitesse</td>
                        <td>⭐⭐⭐⭐⭐</td>
                        <td>⭐⭐⭐</td>
                        <td>⭐⭐⭐⭐⭐</td>
                      </tr>
                      <tr>
                        <td>Analyse d'images</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>
                      <tr>
                        <td>Navigation web</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>
                      <tr>
                        <td>DALL-E 3 (images)</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>
                      <tr>
                        <td>Code Interpreter</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>
                      <tr>
                        <td>GPTs personnalisés</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>
                      <tr>
                        <td>Conversation vocale</td>
                        <td>Basique</td>
                        <td>Basique</td>
                        <td>Avancée</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="recommendation-box">
                  <h4>🎯 Notre recommandation</h4>
                  <div className="recommendation-content">
                    <div className="rec-item">
                      <strong>Usage occasionnel / Découverte :</strong>
                      <span>GPT-3.5 gratuit est suffisant</span>
                    </div>
                    <div className="rec-item">
                      <strong>Usage régulier / Professionnel :</strong>
                      <span>ChatGPT Plus à 20$/mois (GPT-4o)</span>
                    </div>
                    <div className="rec-item">
                      <strong>Entreprise / Équipe :</strong>
                      <span>ChatGPT Team ou Enterprise</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 3: INSTALLATION ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('installation')}>
              <div className="section-toggle-content">
                <span className="section-number">03</span>
                <div>
                  <h2>Comment accéder à ChatGPT</h2>
                  <p>Guide pas à pas pour démarrer en 2 minutes</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.installation ? '▼' : '▶'}</span>
            </div>

            {expandedSections.installation && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>3 façons d'utiliser ChatGPT</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="installation" />
                </div>

                <div className="access-methods">
                  <div className="access-card">
                    <div className="access-icon">🌐</div>
                    <h4>Via le navigateur web</h4>
                    <p>La méthode la plus simple</p>
                    <div className="steps-list">
                      <div className="step-item">
                        <span className="step-num">1</span>
                        <span>Allez sur <strong>chat.openai.com</strong></span>
                      </div>
                      <div className="step-item">
                        <span className="step-num">2</span>
                        <span>Cliquez sur "Sign up" (ou "Log in" si déjà inscrit)</span>
                      </div>
                      <div className="step-item">
                        <span className="step-num">3</span>
                        <span>Créez un compte avec email ou Google/Microsoft/Apple</span>
                      </div>
                      <div className="step-item">
                        <span className="step-num">4</span>
                        <span>Vérifiez votre email</span>
                      </div>
                      <div className="step-item">
                        <span className="step-num">5</span>
                        <span>C'est prêt ! Commencez à discuter</span>
                      </div>
                    </div>
                  </div>

                  <div className="access-card">
                    <div className="access-icon">📱</div>
                    <h4>Application mobile</h4>
                    <p>ChatGPT dans votre poche</p>
                    <div className="steps-list">
                      <div className="step-item">
                        <span className="step-num">1</span>
                        <span>Téléchargez l'app officielle "ChatGPT" sur App Store ou Play Store</span>
                      </div>
                      <div className="step-item">
                        <span className="step-num">2</span>
                        <span>Connectez-vous avec votre compte OpenAI</span>
                      </div>
                      <div className="step-item">
                        <span className="step-num">3</span>
                        <span>Profitez de la conversation vocale !</span>
                      </div>
                    </div>
                    <div className="tip-inline">
                      💡 L'app permet de parler directement à ChatGPT comme à un assistant vocal
                    </div>
                  </div>

                  <div className="access-card">
                    <div className="access-icon">💻</div>
                    <h4>Application desktop (Mac/Windows)</h4>
                    <p>Accès rapide depuis votre ordinateur</p>
                    <div className="steps-list">
                      <div className="step-item">
                        <span className="step-num">1</span>
                        <span>Téléchargez sur openai.com/chatgpt/download</span>
                      </div>
                      <div className="step-item">
                        <span className="step-num">2</span>
                        <span>Installez l'application</span>
                      </div>
                      <div className="step-item">
                        <span className="step-num">3</span>
                        <span>Raccourci : Alt+Space (Windows) ou Opt+Space (Mac)</span>
                      </div>
                    </div>
                    <div className="tip-inline">
                      💡 L'app desktop permet de capturer votre écran et d'analyser ce que vous voyez
                    </div>
                  </div>
                </div>

                <h4>Comment passer à ChatGPT Plus ?</h4>
                <div className="upgrade-box">
                  <div className="upgrade-steps">
                    <div className="upgrade-step">
                      <span className="upgrade-num">1</span>
                      <div>
                        <strong>Connectez-vous</strong>
                        <p>Allez sur chat.openai.com et connectez-vous</p>
                      </div>
                    </div>
                    <div className="upgrade-step">
                      <span className="upgrade-num">2</span>
                      <div>
                        <strong>Cliquez sur "Upgrade to Plus"</strong>
                        <p>Bouton en bas à gauche de l'écran</p>
                      </div>
                    </div>
                    <div className="upgrade-step">
                      <span className="upgrade-num">3</span>
                      <div>
                        <strong>Choisissez votre plan</strong>
                        <p>Plus (20$/mois) ou Team (25$/user/mois)</p>
                      </div>
                    </div>
                    <div className="upgrade-step">
                      <span className="upgrade-num">4</span>
                      <div>
                        <strong>Payez par carte</strong>
                        <p>Visa, Mastercard, ou autres</p>
                      </div>
                    </div>
                  </div>
                  <div className="upgrade-benefits">
                    <h5>Ce que vous obtenez avec Plus :</h5>
                    <ul>
                      <li>✅ GPT-4 et GPT-4o (les plus intelligents)</li>
                      <li>✅ DALL-E 3 (génération d'images)</li>
                      <li>✅ Navigation web en temps réel</li>
                      <li>✅ Analyse de fichiers (PDF, Excel, images...)</li>
                      <li>✅ Création de GPTs personnalisés</li>
                      <li>✅ Accès prioritaire (pas de file d'attente)</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 4: L'INTERFACE ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('interface')}>
              <div className="section-toggle-content">
                <span className="section-number">04</span>
                <div>
                  <h2>Maîtriser l'interface</h2>
                  <p>Toutes les fonctionnalités de ChatGPT expliquées</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.interface ? '▼' : '▶'}</span>
            </div>

            {expandedSections.interface && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Tour de l'interface ChatGPT</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="interface" />
                </div>

                <div className="interface-guide">
                  <div className="interface-section">
                    <h4>📋 Barre latérale gauche</h4>
                    <ul>
                      <li><strong>Nouvelle conversation :</strong> Démarre un nouveau chat (les anciens sont sauvegardés)</li>
                      <li><strong>Historique :</strong> Retrouvez toutes vos conversations passées</li>
                      <li><strong>Recherche :</strong> Cherchez dans vos anciens chats</li>
                      <li><strong>Dossiers :</strong> Organisez vos conversations par projet/thème</li>
                    </ul>
                  </div>

                  <div className="interface-section">
                    <h4>⚙️ Sélecteur de modèle (en haut)</h4>
                    <ul>
                      <li><strong>GPT-4o :</strong> Le modèle recommandé (rapide + intelligent)</li>
                      <li><strong>GPT-4 :</strong> Plus réfléchi, meilleur pour les tâches complexes</li>
                      <li><strong>GPT-3.5 :</strong> Rapide mais moins précis</li>
                    </ul>
                  </div>

                  <div className="interface-section">
                    <h4>📎 Zone de saisie (en bas)</h4>
                    <ul>
                      <li><strong>Trombone 📎 :</strong> Joindre des fichiers (PDF, images, Excel, Word...)</li>
                      <li><strong>Micro 🎤 :</strong> Dicter votre message vocalement</li>
                      <li><strong>Globe 🌐 :</strong> Activer la recherche web</li>
                      <li><strong>Image 🖼️ :</strong> Générer des images avec DALL-E</li>
                    </ul>
                  </div>
                </div>

                <h4>Fonctionnalités avancées (Plus)</h4>
                <div className="features-grid">
                  <div className="feature-box">
                    <div className="feature-icon">🌐</div>
                    <h5>Navigation Web</h5>
                    <p>ChatGPT peut chercher des infos actuelles sur internet. Parfait pour les news, prix, disponibilités...</p>
                    <div className="feature-prompt">
                      <strong>Exemple :</strong> "Quelles sont les dernières actualités sur l'IA cette semaine ?"
                    </div>
                  </div>

                  <div className="feature-box">
                    <div className="feature-icon">🖼️</div>
                    <h5>DALL-E 3 (Images)</h5>
                    <p>Générez des images à partir de descriptions textuelles. Logos, illustrations, photos réalistes...</p>
                    <div className="feature-prompt">
                      <strong>Exemple :</strong> "Crée une image d'un chat astronaute sur la lune, style réaliste"
                    </div>
                  </div>

                  <div className="feature-box">
                    <div className="feature-icon">📊</div>
                    <h5>Analyse de données</h5>
                    <p>Uploadez un fichier Excel/CSV et demandez des analyses, graphiques, insights...</p>
                    <div className="feature-prompt">
                      <strong>Exemple :</strong> "Analyse ce fichier de ventes et crée un graphique des tendances"
                    </div>
                  </div>

                  <div className="feature-box">
                    <div className="feature-icon">📄</div>
                    <h5>Lecture de documents</h5>
                    <p>Uploadez des PDF, Word, etc. ChatGPT les lit et répond à vos questions dessus.</p>
                    <div className="feature-prompt">
                      <strong>Exemple :</strong> "Résume ce contrat en 5 points clés et identifie les risques"
                    </div>
                  </div>

                  <div className="feature-box">
                    <div className="feature-icon">👁️</div>
                    <h5>Vision (analyse d'images)</h5>
                    <p>Uploadez une image et ChatGPT l'analyse : texte, objets, personnes, graphiques...</p>
                    <div className="feature-prompt">
                      <strong>Exemple :</strong> "Qu'est-ce qu'il y a sur cette image ? Décris en détail."
                    </div>
                  </div>

                  <div className="feature-box">
                    <div className="feature-icon">🤖</div>
                    <h5>GPTs personnalisés</h5>
                    <p>Créez votre propre version de ChatGPT spécialisée pour une tâche précise.</p>
                    <div className="feature-prompt">
                      <strong>Exemple :</strong> Un GPT "Expert SEO" qui analyse vos articles et suggère des améliorations
                    </div>
                  </div>
                </div>

                <div className="tip-box">
                  <div className="tip-icon">⌨️</div>
                  <div className="tip-content">
                    <h4>Raccourcis clavier utiles</h4>
                    <ul>
                      <li><strong>Entrée :</strong> Envoyer le message</li>
                      <li><strong>Shift + Entrée :</strong> Aller à la ligne (sans envoyer)</li>
                      <li><strong>Ctrl + Shift + C :</strong> Copier la dernière réponse</li>
                      <li><strong>Ctrl + / :</strong> Ouvrir les raccourcis</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 5: MÉTHODE CRISP ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('crisp')}>
              <div className="section-toggle-content">
                <span className="section-number">05</span>
                <div>
                  <h2>L'Art du Prompt Parfait</h2>
                  <p>La méthode CRISP pour obtenir exactement ce que vous voulez</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.crisp ? '▼' : '▶'}</span>
            </div>

            {expandedSections.crisp && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Pourquoi vos prompts ne fonctionnent pas</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="crisp" />
                </div>

                <div className="warning-box">
                  <strong>❌ Le problème #1 :</strong> 90% des gens écrivent des prompts trop vagues.
                  "Écris-moi un email" donnera un résultat générique. ChatGPT n'est pas devin !
                </div>

                <h4>La méthode CRISP en 5 étapes</h4>
                <div className="crisp-grid">
                  <div className="crisp-card">
                    <div className="crisp-letter">C</div>
                    <div className="crisp-content">
                      <h4>Contexte</h4>
                      <p>Donnez le maximum d'informations sur votre situation</p>
                      <ul className="crisp-details">
                        <li>Qui êtes-vous ? (métier, secteur, niveau)</li>
                        <li>Quelle est la situation ?</li>
                        <li>Pour qui/quoi est-ce destiné ?</li>
                        <li>Quel est l'historique ?</li>
                      </ul>
                      <span className="crisp-example">"Je suis responsable marketing dans une startup SaaS B2B. Nous lançons un nouveau produit d'automatisation..."</span>
                    </div>
                  </div>

                  <div className="crisp-card">
                    <div className="crisp-letter">R</div>
                    <div className="crisp-content">
                      <h4>Rôle</h4>
                      <p>Demandez à ChatGPT d'incarner un expert</p>
                      <ul className="crisp-details">
                        <li>Expert dans quel domaine ?</li>
                        <li>Avec quelle expérience ?</li>
                        <li>Quel style/approche ?</li>
                      </ul>
                      <span className="crisp-example">"Agis comme un copywriter senior spécialisé en B2B avec 15 ans d'expérience"</span>
                    </div>
                  </div>

                  <div className="crisp-card">
                    <div className="crisp-letter">I</div>
                    <div className="crisp-content">
                      <h4>Instructions</h4>
                      <p>Soyez ultra-précis sur ce que vous voulez</p>
                      <ul className="crisp-details">
                        <li>Quelle action exacte ?</li>
                        <li>Combien d'éléments ?</li>
                        <li>Quels critères respecter ?</li>
                      </ul>
                      <span className="crisp-example">"Rédige 5 objets d'email de prospection qui génèrent de la curiosité sans être clickbait"</span>
                    </div>
                  </div>

                  <div className="crisp-card">
                    <div className="crisp-letter">S</div>
                    <div className="crisp-content">
                      <h4>Spécifications</h4>
                      <p>Définissez le format et les contraintes</p>
                      <ul className="crisp-details">
                        <li>Longueur (mots, phrases, pages)</li>
                        <li>Format (liste, tableau, paragraphes)</li>
                        <li>Ton (formel, décontracté, technique)</li>
                        <li>Langue et niveau</li>
                      </ul>
                      <span className="crisp-example">"Max 50 caractères par objet, ton professionnel mais pas corporate, en français"</span>
                    </div>
                  </div>

                  <div className="crisp-card">
                    <div className="crisp-letter">P</div>
                    <div className="crisp-content">
                      <h4>Personnalisation</h4>
                      <p>Ajoutez des exemples et références</p>
                      <ul className="crisp-details">
                        <li>Exemples de ce que vous aimez</li>
                        <li>Ce que vous ne voulez PAS</li>
                        <li>Marques/styles à imiter</li>
                      </ul>
                      <span className="crisp-example">"Inspire-toi du style de Lemlist. Évite les formules comme 'J'espère que vous allez bien'"</span>
                    </div>
                  </div>
                </div>

                <h4>Générateur de prompts CRISP</h4>
                <p>Remplissez les champs et obtenez un prompt optimisé :</p>
                <PromptPlayground
                  variables={crispVariables}
                  template={crispTemplate}
                  title="Votre prompt CRISP"
                />

                <h4>Avant / Après : La magie CRISP</h4>
                <div className="before-after">
                  <div className="before">
                    <h5>❌ Prompt amateur</h5>
                    <PromptBox prompt="Écris-moi un post LinkedIn" title="Mauvais" />
                    <p className="result-preview">Résultat : Post générique, passe-partout, sans personnalité</p>
                  </div>
                  <div className="after">
                    <h5>✅ Prompt CRISP</h5>
                    <PromptBox prompt={`CONTEXTE : Je suis CEO d'une startup fintech. Nous venons de lever 2M€. Je veux annoncer ça sur LinkedIn pour attirer des talents.

RÔLE : Agis comme un expert en personal branding LinkedIn.

INSTRUCTIONS : Rédige un post d'annonce de levée de fonds qui :
- Raconte l'histoire (d'où on vient, pourquoi cette levée)
- Montre l'ambition sans arrogance
- Incite les talents à nous rejoindre
- Génère de l'engagement

SPÉCIFICATIONS :
- 1200-1500 caractères
- Accroche percutante (pas de "Très heureux de...")
- Emojis avec modération
- Appel à l'action clair

PERSONNALISATION :
- Ton : authentique, humble mais ambitieux
- Éviter : jargon corporate, formules clichés
- Style : storytelling personnel`} title="Excellent" />
                    <p className="result-preview">Résultat : Post viral, authentique, qui génère des candidatures</p>
                  </div>
                </div>

                <h4>10 astuces de pro pour vos prompts</h4>
                <div className="tips-grid">
                  <div className="pro-tip">
                    <span className="tip-num">1</span>
                    <div>
                      <strong>Demandez plusieurs versions</strong>
                      <p>"Donne-moi 3 versions différentes" pour choisir la meilleure</p>
                    </div>
                  </div>
                  <div className="pro-tip">
                    <span className="tip-num">2</span>
                    <div>
                      <strong>Itérez progressivement</strong>
                      <p>"C'est bien mais rends-le plus [concis/percutant/technique]"</p>
                    </div>
                  </div>
                  <div className="pro-tip">
                    <span className="tip-num">3</span>
                    <div>
                      <strong>Donnez des exemples</strong>
                      <p>"Voici un exemple de ce que j'aime : [exemple]"</p>
                    </div>
                  </div>
                  <div className="pro-tip">
                    <span className="tip-num">4</span>
                    <div>
                      <strong>Demandez une structure</strong>
                      <p>"Réponds avec : 1) Résumé 2) Détails 3) Actions"</p>
                    </div>
                  </div>
                  <div className="pro-tip">
                    <span className="tip-num">5</span>
                    <div>
                      <strong>Utilisez le "step by step"</strong>
                      <p>"Réfléchis étape par étape avant de répondre"</p>
                    </div>
                  </div>
                  <div className="pro-tip">
                    <span className="tip-num">6</span>
                    <div>
                      <strong>Demandez des critiques</strong>
                      <p>"Maintenant critique ta réponse et améliore-la"</p>
                    </div>
                  </div>
                  <div className="pro-tip">
                    <span className="tip-num">7</span>
                    <div>
                      <strong>Spécifiez le public</strong>
                      <p>"Explique comme si je suis [débutant/expert/enfant de 10 ans]"</p>
                    </div>
                  </div>
                  <div className="pro-tip">
                    <span className="tip-num">8</span>
                    <div>
                      <strong>Demandez des questions</strong>
                      <p>"Avant de répondre, pose-moi les questions dont tu as besoin"</p>
                    </div>
                  </div>
                  <div className="pro-tip">
                    <span className="tip-num">9</span>
                    <div>
                      <strong>Utilisez les métaphores</strong>
                      <p>"Explique [concept] avec une analogie du quotidien"</p>
                    </div>
                  </div>
                  <div className="pro-tip">
                    <span className="tip-num">10</span>
                    <div>
                      <strong>Demandez le "pourquoi"</strong>
                      <p>"Explique ton raisonnement" pour comprendre la logique</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 6: CAS D'USAGE ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('usecases')}>
              <div className="section-toggle-content">
                <span className="section-number">06</span>
                <div>
                  <h2>Que faire avec ChatGPT ?</h2>
                  <p>100+ cas d'usage concrets par domaine</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.usecases ? '▼' : '▶'}</span>
            </div>

            {expandedSections.usecases && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>ChatGPT peut tout faire (ou presque)</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="usecases" />
                </div>

                <div className="usecases-tabs">
                  <div className="usecase-category">
                    <h4>✍️ Rédaction & Contenu</h4>
                    <div className="usecase-list">
                      <div className="usecase-item">
                        <span className="usecase-name">Emails professionnels</span>
                        <span className="usecase-desc">Prospection, relance, négociation, réclamation...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Posts réseaux sociaux</span>
                        <span className="usecase-desc">LinkedIn, Instagram, Twitter, Facebook...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Articles de blog</span>
                        <span className="usecase-desc">SEO, thought leadership, tutoriels...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Scripts vidéo</span>
                        <span className="usecase-desc">YouTube, TikTok, formations, pubs...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Newsletters</span>
                        <span className="usecase-desc">Idées, rédaction, optimisation...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Pages de vente</span>
                        <span className="usecase-desc">Landing pages, fiches produit...</span>
                      </div>
                    </div>
                    <PromptBox prompt={`Tu es un copywriter expert en conversion.

Rédige une page de vente pour [PRODUIT/SERVICE].

Audience cible : [DESCRIPTION]
Problème principal : [DOULEUR]
Solution : [BÉNÉFICE CLÉ]

Structure :
1. Accroche qui interpelle
2. Amplification du problème
3. Présentation de la solution
4. Bénéfices (pas features)
5. Preuve sociale
6. Offre irrésistible
7. Garantie
8. Appel à l'action

Ton : persuasif mais authentique
Longueur : 800-1000 mots`} title="Prompt : Page de vente" />
                  </div>

                  <div className="usecase-category">
                    <h4>💼 Business & Stratégie</h4>
                    <div className="usecase-list">
                      <div className="usecase-item">
                        <span className="usecase-name">Business plan</span>
                        <span className="usecase-desc">Structure, projections, stratégie...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Étude de marché</span>
                        <span className="usecase-desc">Analyse concurrence, tendances, opportunités...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Pitch deck</span>
                        <span className="usecase-desc">Structure, storytelling, slides clés...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Brainstorming</span>
                        <span className="usecase-desc">Idées de produits, noms, stratégies...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">SWOT / Porter</span>
                        <span className="usecase-desc">Analyses stratégiques complètes</span>
                      </div>
                    </div>
                    <PromptBox prompt={`Tu es un consultant McKinsey avec 20 ans d'expérience.

Analyse mon business et donne-moi un diagnostic complet :

MON BUSINESS :
[Description de votre activité]

MON MARCHÉ :
[Description du marché, concurrents]

MES CHIFFRES :
[CA, marge, croissance si disponible]

Fais :
1. Analyse SWOT détaillée
2. 5 Forces de Porter
3. 3 opportunités de croissance
4. 3 menaces à anticiper
5. Recommandation stratégique #1 à court terme

Format : sections claires avec bullet points`} title="Prompt : Diagnostic stratégique" />
                  </div>

                  <div className="usecase-category">
                    <h4>📊 Données & Analyse</h4>
                    <div className="usecase-list">
                      <div className="usecase-item">
                        <span className="usecase-name">Analyse Excel/CSV</span>
                        <span className="usecase-desc">Insights, tendances, anomalies...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Création de graphiques</span>
                        <span className="usecase-desc">Visualisations automatiques</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Formules Excel</span>
                        <span className="usecase-desc">VLOOKUP, Pivot, formules complexes...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">SQL queries</span>
                        <span className="usecase-desc">Requêtes, optimisation, debug...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Rapports automatisés</span>
                        <span className="usecase-desc">Synthèses de données périodiques</span>
                      </div>
                    </div>
                  </div>

                  <div className="usecase-category">
                    <h4>💻 Comprendre & Apprendre le Code</h4>
                    <div className="info-box" style={{marginBottom: '16px'}}>
                      <p><strong>💡 Note :</strong> ChatGPT est idéal pour <strong>comprendre</strong> et <strong>apprendre</strong> le code.
                      Pour <strong>créer des projets complets</strong> (sites web, applications), utilisez <strong>Claude Code</strong>.</p>
                    </div>
                    <div className="usecase-list">
                      <div className="usecase-item">
                        <span className="usecase-name">Expliquer du code</span>
                        <span className="usecase-desc">Comprendre du code existant ligne par ligne</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Débugger</span>
                        <span className="usecase-desc">Comprendre et corriger les erreurs</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Apprendre à coder</span>
                        <span className="usecase-desc">Tuteur patient et disponible 24/7</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Formules Excel/SQL</span>
                        <span className="usecase-desc">Créer des formules complexes</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Scripts simples</span>
                        <span className="usecase-desc">Petits automatismes ponctuels</span>
                      </div>
                    </div>
                    <PromptBox prompt={`Tu es un expert en [LANGAGE].

J'ai ce code que je ne comprends pas :
\`\`\`
[COLLER VOTRE CODE]
\`\`\`

1. Explique-moi ce que fait ce code ligne par ligne
2. Simplifie l'explication pour un débutant
3. Donne-moi des exemples d'utilisation`} title="Prompt : Comprendre du code" />
                  </div>

                  <div className="usecase-category">
                    <h4>🎓 Apprentissage</h4>
                    <div className="usecase-list">
                      <div className="usecase-item">
                        <span className="usecase-name">Expliquer des concepts</span>
                        <span className="usecase-desc">Comme un prof patient et clair</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Créer des quiz</span>
                        <span className="usecase-desc">Tester ses connaissances</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Résumer des livres</span>
                        <span className="usecase-desc">Points clés, insights...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Préparer des examens</span>
                        <span className="usecase-desc">Fiches révision, questions probables...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Apprendre une langue</span>
                        <span className="usecase-desc">Conversation, grammaire, vocabulaire...</span>
                      </div>
                    </div>
                    <PromptBox prompt={`Tu es un professeur expert qui sait expliquer simplement les concepts complexes.

Explique-moi : [CONCEPT/SUJET]

Mon niveau actuel : [débutant/intermédiaire/avancé]
Mon objectif : [pourquoi j'apprends ça]

Structure ta réponse :
1. Définition simple (1 phrase, comme pour un enfant de 10 ans)
2. Analogie du quotidien
3. Les 5 points essentiels à retenir
4. Exemple concret d'application
5. Les 3 erreurs courantes à éviter
6. Pour aller plus loin : ressources recommandées

Utilise des emojis pour rendre ça plus visuel.`} title="Prompt : Apprendre un concept" />
                  </div>

                  <div className="usecase-category">
                    <h4>🎨 Créativité</h4>
                    <div className="usecase-list">
                      <div className="usecase-item">
                        <span className="usecase-name">Génération d'images</span>
                        <span className="usecase-desc">DALL-E 3 pour logos, visuels, photos...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Noms de marque</span>
                        <span className="usecase-desc">Brainstorming créatif</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Slogans/taglines</span>
                        <span className="usecase-desc">Accroches mémorables</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Histoires/scénarios</span>
                        <span className="usecase-desc">Fiction, storytelling...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Musique/paroles</span>
                        <span className="usecase-desc">Chansons, jingles...</span>
                      </div>
                    </div>
                  </div>

                  <div className="usecase-category">
                    <h4>🏠 Quotidien</h4>
                    <div className="usecase-list">
                      <div className="usecase-item">
                        <span className="usecase-name">Planification voyage</span>
                        <span className="usecase-desc">Itinéraires, budgets, conseils...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Recettes cuisine</span>
                        <span className="usecase-desc">Selon ingrédients, régime, temps...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Conseils santé</span>
                        <span className="usecase-desc">Sport, nutrition, bien-être...</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Comparaison produits</span>
                        <span className="usecase-desc">Aide à la décision d'achat</span>
                      </div>
                      <div className="usecase-item">
                        <span className="usecase-name">Rédiger des lettres</span>
                        <span className="usecase-desc">Admin, réclamations, courriers...</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 7: TECHNIQUES AVANCÉES ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('advanced')}>
              <div className="section-toggle-content">
                <span className="section-number">07</span>
                <div>
                  <h2>Techniques Avancées</h2>
                  <p>Débloquez la pleine puissance de ChatGPT</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.advanced ? '▼' : '▶'}</span>
            </div>

            {expandedSections.advanced && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Passez au niveau expert</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="advanced" />
                </div>

                <div className="advanced-techniques">
                  <div className="technique-card">
                    <h4>🔗 Chain of Thought (Réflexion en chaîne)</h4>
                    <p>Forcez ChatGPT à réfléchir étape par étape pour des réponses plus précises.</p>
                    <PromptBox prompt={`Résous ce problème étape par étape. Montre ton raisonnement à chaque étape avant de donner la réponse finale.

Problème : [VOTRE PROBLÈME]

Réfléchis à voix haute en suivant ces étapes :
1. Qu'est-ce qui est demandé exactement ?
2. Quelles informations ai-je ?
3. Quelle approche utiliser ?
4. Application étape par étape
5. Vérification du résultat
6. Réponse finale`} title="Chain of Thought" />
                  </div>

                  <div className="technique-card">
                    <h4>🎭 Role-Playing avancé</h4>
                    <p>Faites jouer plusieurs rôles à ChatGPT pour des perspectives multiples.</p>
                    <PromptBox prompt={`Je veux que tu simules un débat entre 3 experts sur : [SUJET]

Les 3 experts :
1. Le POUR - avocat passionné de cette idée
2. Le CONTRE - critique sceptique
3. Le MODÉRÉ - synthétise et nuance

Chaque expert donne 3 arguments.
Puis synthétise les meilleures conclusions.`} title="Multi-perspectives" />
                  </div>

                  <div className="technique-card">
                    <h4>🔄 Auto-amélioration</h4>
                    <p>Faites critiquer et améliorer ses propres réponses.</p>
                    <PromptBox prompt={`1. Réponds d'abord à cette question : [VOTRE QUESTION]

2. Maintenant, critique ta propre réponse :
   - Qu'est-ce qui manque ?
   - Qu'est-ce qui pourrait être plus clair ?
   - Y a-t-il des erreurs ou biais ?

3. Enfin, donne une version améliorée de ta réponse.`} title="Self-refinement" />
                  </div>

                  <div className="technique-card">
                    <h4>📝 Few-shot learning</h4>
                    <p>Donnez des exemples pour que ChatGPT comprenne exactement ce que vous voulez.</p>
                    <PromptBox prompt={`Transforme ces titres en accroches percutantes.

EXEMPLES :
Titre : "Comment perdre du poids"
Accroche : "J'ai perdu 15kg en 3 mois sans régime - voici mon secret"

Titre : "Apprendre l'anglais"
Accroche : "De 0 à fluent en 6 mois : la méthode que personne n'enseigne"

MAINTENANT, fais pareil pour :
Titre : "[VOTRE TITRE]"
Accroche : `} title="Few-shot prompting" />
                  </div>

                  <div className="technique-card">
                    <h4>🎯 Prompt chaining</h4>
                    <p>Décomposez les tâches complexes en étapes successives.</p>
                    <div className="chain-steps">
                      <div className="chain-step">
                        <span className="chain-num">1</span>
                        <div>
                          <strong>D'abord : Recherche</strong>
                          <p>"Liste les 10 tendances clés du marché [X] en 2024"</p>
                        </div>
                      </div>
                      <div className="chain-step">
                        <span className="chain-num">2</span>
                        <div>
                          <strong>Ensuite : Analyse</strong>
                          <p>"Parmi ces tendances, lesquelles représentent la plus grande opportunité pour [mon business] ?"</p>
                        </div>
                      </div>
                      <div className="chain-step">
                        <span className="chain-num">3</span>
                        <div>
                          <strong>Puis : Stratégie</strong>
                          <p>"Crée un plan d'action en 30 jours pour capitaliser sur [tendance choisie]"</p>
                        </div>
                      </div>
                      <div className="chain-step">
                        <span className="chain-num">4</span>
                        <div>
                          <strong>Enfin : Exécution</strong>
                          <p>"Rédige le premier livrable de ce plan : [email/post/page...]"</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="technique-card">
                    <h4>🧠 Custom Instructions</h4>
                    <p>Configurez ChatGPT une fois pour qu'il vous connaisse et réponde toujours de façon personnalisée.</p>
                    <div className="custom-instructions-guide">
                      <p><strong>Où les configurer :</strong> Cliquez sur votre profil → Personnaliser ChatGPT</p>
                      <PromptBox prompt={`[À METTRE DANS "Ce que ChatGPT doit savoir sur vous"]

Je suis [MÉTIER] chez [ENTREPRISE/SECTEUR].
Mon expertise : [DOMAINES]
Mes objectifs actuels : [OBJECTIFS]
Mon niveau technique : [NIVEAU]
Ma langue préférée : Français

[À METTRE DANS "Comment ChatGPT doit répondre"]

- Sois direct et concis, évite le fluff
- Donne des exemples concrets
- Structure avec des bullet points
- Propose des actions concrètes
- Challenge mes idées si nécessaire
- Utilise un ton professionnel mais accessible`} title="Template Custom Instructions" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 8: UTILISATION PRO ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('work')}>
              <div className="section-toggle-content">
                <span className="section-number">08</span>
                <div>
                  <h2>ChatGPT pour le Travail</h2>
                  <p>Gagnez des heures chaque semaine</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.work ? '▼' : '▶'}</span>
            </div>

            {expandedSections.work && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Automatisez votre quotidien professionnel</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="work" />
                </div>

                <div className="time-savings">
                  <h4>⏱️ Temps moyen économisé par tâche</h4>
                  <div className="savings-grid">
                    <div className="saving-item">
                      <span className="saving-task">Rédaction d'emails</span>
                      <span className="saving-time">-70%</span>
                    </div>
                    <div className="saving-item">
                      <span className="saving-task">Rapports & synthèses</span>
                      <span className="saving-time">-60%</span>
                    </div>
                    <div className="saving-item">
                      <span className="saving-task">Préparation réunions</span>
                      <span className="saving-time">-50%</span>
                    </div>
                    <div className="saving-item">
                      <span className="saving-task">Recherche d'informations</span>
                      <span className="saving-time">-80%</span>
                    </div>
                    <div className="saving-item">
                      <span className="saving-task">Création de présentations</span>
                      <span className="saving-time">-40%</span>
                    </div>
                    <div className="saving-item">
                      <span className="saving-task">Correction/relecture</span>
                      <span className="saving-time">-90%</span>
                    </div>
                  </div>
                </div>

                <h4>Prompts pour le quotidien pro</h4>

                <PromptBox prompt={`Tu es mon assistant de productivité.

Voici ma liste de tâches pour aujourd'hui :
[COLLER VOTRE LISTE]

1. Priorise ces tâches (urgent/important vs reste)
2. Estime le temps pour chacune
3. Propose un planning optimisé
4. Identifie ce qui peut être délégué ou automatisé
5. Suggère les tâches à reporter si je manque de temps`} title="Planification de journée" />

                <PromptBox prompt={`Voici mes notes brutes de réunion :
[COLLER VOS NOTES]

Transforme-les en compte-rendu professionnel avec :
1. Participants et date
2. Points clés discutés (bullet points)
3. Décisions prises
4. Actions à faire (QUI fait QUOI pour QUAND)
5. Points en suspens
6. Date de la prochaine réunion si mentionnée`} title="Compte-rendu de réunion" />

                <PromptBox prompt={`Je dois envoyer un email difficile.

Situation : [DÉCRIRE LA SITUATION DÉLICATE]
Destinataire : [QUI]
Mon objectif : [CE QUE JE VEUX OBTENIR]
Ton souhaité : [ferme mais respectueux / diplomatique / direct...]

Rédige cet email en :
- Restant professionnel
- Évitant l'agressivité passive
- Étant clair sur mes attentes
- Laissant une porte de sortie à l'autre

Propose 2 versions : une plus directe, une plus diplomatique.`} title="Email difficile" />

                <PromptBox prompt={`Je dois faire une présentation sur [SUJET] pour [AUDIENCE].

Durée : [X] minutes
Objectif : [informer / convaincre / former / vendre]

Crée-moi :
1. Le plan détaillé (titres de slides)
2. Les messages clés de chaque slide (2-3 bullet points max)
3. Une idée de visuel/schéma pour les slides importantes
4. Les questions probables de l'audience
5. Une accroche percutante pour commencer
6. Une conclusion mémorable avec call-to-action`} title="Créer une présentation" />

                <div className="tip-box">
                  <div className="tip-icon">💡</div>
                  <div className="tip-content">
                    <h4>Workflow ultime</h4>
                    <p>Créez un GPT personnalisé avec vos templates, votre ton, vos préférences. Vous n'aurez plus qu'à lui donner les infos brutes et il produira des documents professionnels en quelques secondes.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 9: PROJETS ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('projects')}>
              <div className="section-toggle-content">
                <span className="section-number">09</span>
                <div>
                  <h2>Projets Concrets avec ChatGPT</h2>
                  <p>Des idées de projets à réaliser maintenant</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.projects ? '▼' : '▶'}</span>
            </div>

            {expandedSections.projects && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Passez à l'action</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="projects" />
                </div>

                <div className="projects-grid">
                  <div className="project-card beginner">
                    <div className="project-level">Débutant</div>
                    <h4>📧 Système d'emails automatisés</h4>
                    <p>Créez vos templates d'emails pour toutes les situations</p>
                    <ul>
                      <li>5 emails de prospection</li>
                      <li>5 emails de relance</li>
                      <li>5 emails de remerciement</li>
                      <li>Sauvegardez-les dans un doc</li>
                    </ul>
                    <div className="project-time">⏱️ 1-2 heures</div>
                  </div>

                  <div className="project-card beginner">
                    <div className="project-level">Débutant</div>
                    <h4>📱 30 jours de contenu social</h4>
                    <p>Planifiez un mois complet de posts</p>
                    <ul>
                      <li>Définissez 4 piliers de contenu</li>
                      <li>Générez 30 idées de posts</li>
                      <li>Rédigez les 10 premiers</li>
                      <li>Créez un calendrier éditorial</li>
                    </ul>
                    <div className="project-time">⏱️ 2-3 heures</div>
                  </div>

                  <div className="project-card intermediate">
                    <div className="project-level">Intermédiaire</div>
                    <h4>📚 Votre ebook/guide gratuit</h4>
                    <p>Lead magnet pour capturer des emails</p>
                    <ul>
                      <li>Choisissez un sujet d'expertise</li>
                      <li>Créez le plan (10-15 pages)</li>
                      <li>Rédigez chapitre par chapitre</li>
                      <li>Ajoutez des visuels avec DALL-E</li>
                    </ul>
                    <div className="project-time">⏱️ 4-6 heures</div>
                  </div>

                  <div className="project-card intermediate">
                    <div className="project-level">Intermédiaire</div>
                    <h4>🎬 Script de formation vidéo</h4>
                    <p>Créez une mini-formation de 30 min</p>
                    <ul>
                      <li>Définissez l'objectif pédagogique</li>
                      <li>Créez 5 modules de 5-7 min</li>
                      <li>Rédigez les scripts détaillés</li>
                      <li>Préparez les slides</li>
                    </ul>
                    <div className="project-time">⏱️ 3-4 heures</div>
                  </div>

                  <div className="project-card advanced">
                    <div className="project-level">Avancé</div>
                    <h4>🤖 Votre GPT personnalisé</h4>
                    <p>Un assistant IA sur-mesure pour votre métier</p>
                    <ul>
                      <li>Définissez le cas d'usage précis</li>
                      <li>Écrivez les instructions système</li>
                      <li>Uploadez vos documents de référence</li>
                      <li>Testez et affinez</li>
                    </ul>
                    <div className="project-time">⏱️ 2-3 heures</div>
                  </div>

                  <div className="project-card advanced">
                    <div className="project-level">Avancé</div>
                    <h4>⚡ Automatisation Zapier + ChatGPT</h4>
                    <p>Automatisez un workflow complet</p>
                    <ul>
                      <li>Identifiez une tâche répétitive</li>
                      <li>Créez le workflow dans Zapier</li>
                      <li>Intégrez l'API OpenAI</li>
                      <li>Testez et déployez</li>
                    </ul>
                    <div className="project-time">⏱️ 3-5 heures</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 10: LIMITES ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('limits')}>
              <div className="section-toggle-content">
                <span className="section-number">10</span>
                <div>
                  <h2>Limites et Bonnes Pratiques</h2>
                  <p>Ce que ChatGPT ne peut pas faire + conseils de sécurité</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.limits ? '▼' : '▶'}</span>
            </div>

            {expandedSections.limits && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Utiliser ChatGPT de façon responsable</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="limits" />
                </div>

                <h4>❌ Ce que ChatGPT ne peut PAS faire</h4>
                <div className="limits-grid">
                  <div className="limit-card">
                    <div className="limit-icon">🎯</div>
                    <h5>Garantir 100% d'exactitude</h5>
                    <p>ChatGPT peut "halluciner" — inventer des faits qui semblent vrais. Vérifiez toujours les infos importantes.</p>
                  </div>
                  <div className="limit-card">
                    <div className="limit-icon">📅</div>
                    <h5>Connaître l'actualité récente</h5>
                    <p>Sans navigation web activée, ses connaissances s'arrêtent à sa date d'entraînement.</p>
                  </div>
                  <div className="limit-card">
                    <div className="limit-icon">🔐</div>
                    <h5>Garder vos données privées</h5>
                    <p>Ne partagez jamais de données sensibles (mots de passe, infos bancaires, secrets d'entreprise).</p>
                  </div>
                  <div className="limit-card">
                    <div className="limit-icon">🧠</div>
                    <h5>Remplacer l'expertise humaine</h5>
                    <p>Pour les décisions importantes (juridiques, médicales, financières), consultez des professionnels.</p>
                  </div>
                  <div className="limit-card">
                    <div className="limit-icon">💭</div>
                    <h5>Se souvenir entre sessions</h5>
                    <p>Chaque nouvelle conversation repart de zéro (sauf avec la mémoire activée en Plus).</p>
                  </div>
                  <div className="limit-card">
                    <div className="limit-icon">🎨</div>
                    <h5>Être vraiment créatif</h5>
                    <p>Il remixe ce qu'il a appris. L'originalité pure reste humaine.</p>
                  </div>
                </div>

                <h4>✅ Les bonnes pratiques</h4>
                <div className="best-practices">
                  <div className="practice-item">
                    <span className="practice-check">✓</span>
                    <div>
                      <strong>Vérifiez les faits importants</strong>
                      <p>Surtout pour les chiffres, dates, citations, informations légales ou médicales.</p>
                    </div>
                  </div>
                  <div className="practice-item">
                    <span className="practice-check">✓</span>
                    <div>
                      <strong>Ajoutez votre touche personnelle</strong>
                      <p>Utilisez ChatGPT comme base, puis personnalisez avec votre expertise et style.</p>
                    </div>
                  </div>
                  <div className="practice-item">
                    <span className="practice-check">✓</span>
                    <div>
                      <strong>Soyez transparent</strong>
                      <p>Si vous publiez du contenu généré par IA, considérez le mentionner selon le contexte.</p>
                    </div>
                  </div>
                  <div className="practice-item">
                    <span className="practice-check">✓</span>
                    <div>
                      <strong>Protégez vos données</strong>
                      <p>N'entrez jamais d'informations confidentielles. Utilisez des données anonymisées si besoin.</p>
                    </div>
                  </div>
                  <div className="practice-item">
                    <span className="practice-check">✓</span>
                    <div>
                      <strong>Itérez et affinez</strong>
                      <p>Rarement parfait du premier coup. Demandez des modifications jusqu'à satisfaction.</p>
                    </div>
                  </div>
                </div>

                <div className="warning-box">
                  <strong>⚠️ Attention aux "hallucinations"</strong>
                  <p>ChatGPT peut inventer des sources, des statistiques, des citations qui n'existent pas — avec beaucoup d'assurance. C'est le phénomène d'hallucination. Plus le sujet est pointu ou récent, plus le risque est élevé.</p>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 11: PEURS ET MYTHES ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('fears')}>
              <div className="section-toggle-content">
                <span className="section-number">11</span>
                <div>
                  <h2>Peurs, Mythes et Réalités</h2>
                  <p>Réponses honnêtes à vos vraies inquiétudes</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.fears ? '▼' : '▶'}</span>
            </div>

            {expandedSections.fears && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Parlons de ce qui vous inquiète vraiment</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="fears" />
                </div>

                <div className="fears-grid">
                  <div className="fear-card">
                    <div className="fear-question">
                      <span className="fear-icon">😰</span>
                      <h4>"ChatGPT va-t-il me remplacer au travail ?"</h4>
                    </div>
                    <div className="fear-answer">
                      <p><strong>La vérité :</strong> Non, mais quelqu'un qui SAIT utiliser ChatGPT pourrait vous remplacer.</p>
                      <p>L'IA ne remplace pas les humains — elle amplifie ceux qui l'utilisent. Un graphiste avec Midjourney produit 10x plus qu'avant. Un rédacteur avec ChatGPT aussi. La question n'est pas "l'IA ou moi", c'est "moi AVEC l'IA ou moi SANS".</p>
                      <div className="fear-action">
                        <strong>→ Action :</strong> Apprenez maintenant pour être dans le camp des "amplifiés", pas des "remplacés".
                      </div>
                    </div>
                  </div>

                  <div className="fear-card">
                    <div className="fear-question">
                      <span className="fear-icon">🤔</span>
                      <h4>"C'est de la triche d'utiliser l'IA ?"</h4>
                    </div>
                    <div className="fear-answer">
                      <p><strong>La vérité :</strong> Est-ce de la triche d'utiliser une calculatrice ? Excel ? Google ?</p>
                      <p>ChatGPT est un outil. Comme le correcteur orthographique ou le GPS. Ce n'est pas de la triche de l'utiliser intelligemment. La vraie valeur, c'est VOTRE expertise, votre jugement, votre créativité. ChatGPT accélère juste l'exécution.</p>
                      <div className="fear-action">
                        <strong>→ Réalité :</strong> Les meilleurs professionnels utilisent les meilleurs outils. Point.
                      </div>
                    </div>
                  </div>

                  <div className="fear-card">
                    <div className="fear-question">
                      <span className="fear-icon">👀</span>
                      <h4>"Que vont penser mes collègues/mon patron ?"</h4>
                    </div>
                    <div className="fear-answer">
                      <p><strong>La vérité :</strong> Probablement qu'ils l'utilisent déjà en secret.</p>
                      <p>Selon une étude Fishbowl, 43% des professionnels utilisent ChatGPT au travail, mais beaucoup ne le disent pas. Les entreprises les plus innovantes encouragent maintenant son usage. Microsoft, Google, Salesforce l'intègrent tous dans leurs outils.</p>
                      <div className="fear-action">
                        <strong>→ Conseil :</strong> Soyez celui qui forme les autres plutôt que celui qui se cache.
                      </div>
                    </div>
                  </div>

                  <div className="fear-card">
                    <div className="fear-question">
                      <span className="fear-icon">🔒</span>
                      <h4>"Mes données sont-elles en sécurité ?"</h4>
                    </div>
                    <div className="fear-answer">
                      <p><strong>La vérité :</strong> Ça dépend de ce que vous partagez.</p>
                      <p>OpenAI utilise vos conversations pour améliorer ses modèles (désactivable dans les paramètres). Règle d'or : ne partagez JAMAIS de données sensibles (mots de passe, données clients, secrets business). Utilisez des données anonymisées ou fictives.</p>
                      <div className="fear-action">
                        <strong>→ Pour les entreprises :</strong> ChatGPT Enterprise garantit que vos données ne sont pas utilisées pour l'entraînement.
                      </div>
                    </div>
                  </div>

                  <div className="fear-card">
                    <div className="fear-question">
                      <span className="fear-icon">🎓</span>
                      <h4>"Je ne suis pas technique, c'est trop compliqué pour moi"</h4>
                    </div>
                    <div className="fear-answer">
                      <p><strong>La vérité :</strong> Si vous savez envoyer un message, vous savez utiliser ChatGPT.</p>
                      <p>C'est littéralement une conversation. Pas de code, pas de logiciel complexe. Vous tapez en français, il répond en français. 80% des utilisateurs n'ont aucun background technique. C'est conçu pour être accessible à tous.</p>
                      <div className="fear-action">
                        <strong>→ Test :</strong> Allez sur chat.openai.com et posez une question. Vous verrez, c'est simple.
                      </div>
                    </div>
                  </div>

                  <div className="fear-card">
                    <div className="fear-question">
                      <span className="fear-icon">⏰</span>
                      <h4>"J'ai pas le temps d'apprendre un nouvel outil"</h4>
                    </div>
                    <div className="fear-answer">
                      <p><strong>La vérité :</strong> Vous perdez du temps en ne l'utilisant PAS.</p>
                      <p>30 minutes à apprendre = des heures économisées chaque semaine. Un email de 20 minutes se rédige en 2 minutes. Un rapport de 2 heures en 20 minutes. Le ROI est immédiat et massif.</p>
                      <div className="fear-action">
                        <strong>→ Calcul :</strong> Si vous économisez 1h/jour, c'est 250h/an. Soit plus de 6 semaines de travail.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="myth-busters">
                  <h4>🚫 5 Mythes à oublier tout de suite</h4>
                  <div className="myths-list">
                    <div className="myth-item">
                      <span className="myth-false">FAUX</span>
                      <span className="myth-text">"ChatGPT est toujours correct" → Il fait des erreurs, vérifiez toujours les infos importantes</span>
                    </div>
                    <div className="myth-item">
                      <span className="myth-false">FAUX</span>
                      <span className="myth-text">"Il faut être ingénieur pour l'utiliser" → N'importe qui peut l'utiliser en 5 minutes</span>
                    </div>
                    <div className="myth-item">
                      <span className="myth-false">FAUX</span>
                      <span className="myth-text">"C'est juste un gadget" → C'est l'outil le plus transformatif depuis internet</span>
                    </div>
                    <div className="myth-item">
                      <span className="myth-false">FAUX</span>
                      <span className="myth-text">"Ça va disparaître" → L'IA est là pour rester, elle ne fait que s'améliorer</span>
                    </div>
                    <div className="myth-item">
                      <span className="myth-false">FAUX</span>
                      <span className="myth-text">"C'est trop cher" → La version gratuite est déjà extrêmement puissante</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 12: CHATGPT VS AUTRES ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('vsothers')}>
              <div className="section-toggle-content">
                <span className="section-number">12</span>
                <div>
                  <h2>ChatGPT vs Claude vs Gemini vs Copilot</h2>
                  <p>Comparaison honnête : lequel choisir et pourquoi ?</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.vsothers ? '▼' : '▶'}</span>
            </div>

            {expandedSections.vsothers && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Quelle IA pour quel usage ?</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="vsothers" />
                </div>

                <p>Il existe plusieurs IA conversationnelles. Voici une comparaison honnête pour vous aider à choisir.</p>

                <div className="ai-comparison-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Critère</th>
                        <th>ChatGPT</th>
                        <th>Claude</th>
                        <th>Gemini</th>
                        <th>Copilot</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Créateur</strong></td>
                        <td>OpenAI</td>
                        <td>Anthropic</td>
                        <td>Google</td>
                        <td>Microsoft</td>
                      </tr>
                      <tr>
                        <td><strong>Prix gratuit</strong></td>
                        <td>✅ Oui (GPT-3.5)</td>
                        <td>✅ Oui</td>
                        <td>✅ Oui</td>
                        <td>✅ Oui</td>
                      </tr>
                      <tr>
                        <td><strong>Prix premium</strong></td>
                        <td>20$/mois</td>
                        <td>20$/mois</td>
                        <td>20$/mois</td>
                        <td>20$/mois</td>
                      </tr>
                      <tr>
                        <td><strong>Génération d'images</strong></td>
                        <td>✅ DALL-E 3</td>
                        <td>❌ Non</td>
                        <td>✅ Imagen</td>
                        <td>✅ DALL-E</td>
                      </tr>
                      <tr>
                        <td><strong>Navigation web</strong></td>
                        <td>✅ Oui</td>
                        <td>❌ Non</td>
                        <td>✅ Oui</td>
                        <td>✅ Oui (Bing)</td>
                      </tr>
                      <tr>
                        <td><strong>Analyse fichiers</strong></td>
                        <td>✅ Oui</td>
                        <td>✅ Oui</td>
                        <td>✅ Oui</td>
                        <td>✅ Limité</td>
                      </tr>
                      <tr>
                        <td><strong>Longueur contexte</strong></td>
                        <td>128K tokens</td>
                        <td>200K tokens</td>
                        <td>1M tokens</td>
                        <td>128K tokens</td>
                      </tr>
                      <tr>
                        <td><strong>Force principale</strong></td>
                        <td>Polyvalence</td>
                        <td>Rédaction longue</td>
                        <td>Données Google</td>
                        <td>Intégration Office</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="ai-profiles">
                  <div className="ai-profile chatgpt-profile">
                    <h4>🟢 ChatGPT - Le généraliste polyvalent</h4>
                    <p><strong>Idéal si :</strong> Vous voulez UN seul outil pour tout faire</p>
                    <ul>
                      <li>✅ Le plus d'utilisateurs (meilleure communauté, plus de ressources)</li>
                      <li>✅ DALL-E 3 intégré pour les images</li>
                      <li>✅ GPTs personnalisables (Store avec milliers de GPTs)</li>
                      <li>✅ Plugins et intégrations nombreuses</li>
                      <li>⚠️ Peut être verbeux parfois</li>
                    </ul>
                  </div>

                  <div className="ai-profile claude-profile">
                    <h4>🟠 Claude - Le rédacteur de qualité</h4>
                    <p><strong>Idéal si :</strong> Vous faites beaucoup de rédaction longue</p>
                    <ul>
                      <li>✅ Meilleur pour les longs documents (200K tokens)</li>
                      <li>✅ Style d'écriture plus naturel et nuancé</li>
                      <li>✅ Moins d'"hallucinations"</li>
                      <li>✅ Plus éthique et prudent</li>
                      <li>⚠️ Pas d'images ni de navigation web</li>
                    </ul>
                    <div className="info-box" style={{marginTop: '12px', background: 'rgba(139, 92, 246, 0.1)', borderColor: '#8b5cf6'}}>
                      <p><strong>💻 Claude Code :</strong> Version spécialisée pour le <strong>développement de projets</strong> (sites web, applications, automatisations). Voir le parcours Claude Code dans cette formation.</p>
                    </div>
                  </div>

                  <div className="ai-profile gemini-profile">
                    <h4>🔵 Gemini - L'intégré Google</h4>
                    <p><strong>Idéal si :</strong> Vous êtes dans l'écosystème Google</p>
                    <ul>
                      <li>✅ Contexte gigantesque (1M tokens)</li>
                      <li>✅ Intégration Gmail, Docs, Sheets</li>
                      <li>✅ Accès aux données Google en temps réel</li>
                      <li>⚠️ Moins mature que ChatGPT</li>
                      <li>⚠️ Résultats parfois incohérents</li>
                    </ul>
                  </div>

                  <div className="ai-profile copilot-profile">
                    <h4>🟣 Copilot - L'intégré Microsoft</h4>
                    <p><strong>Idéal si :</strong> Vous utilisez Office 365 intensivement</p>
                    <ul>
                      <li>✅ Intégré à Word, Excel, PowerPoint, Teams</li>
                      <li>✅ Recherche Bing intégrée</li>
                      <li>✅ Gratuit avec fonctionnalités GPT-4</li>
                      <li>⚠️ Moins puissant en standalone</li>
                      <li>⚠️ Interface moins intuitive</li>
                    </ul>
                  </div>
                </div>

                <div className="recommendation-box">
                  <h4>🎯 Le workflow F12IA™ pour vos projets</h4>
                  <div className="recommendation-content">
                    <p><strong>Étape 1 - ChatGPT :</strong> Développez votre idée, brainstormez, planifiez, rédigez le cahier des charges, créez le contenu, étudiez le marché.</p>
                    <p><strong>Étape 2 - Claude Code :</strong> Passez à la réalisation technique — créez le site web, l'application, les automatisations.</p>
                    <p><strong>En résumé :</strong> ChatGPT = cerveau (réflexion, idées, planification). Claude Code = mains (création, développement, code).</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 13: TROUBLESHOOTING ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('troubleshoot')}>
              <div className="section-toggle-content">
                <span className="section-number">13</span>
                <div>
                  <h2>Quand ChatGPT ne comprend pas</h2>
                  <p>Comment corriger, itérer et obtenir ce que vous voulez</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.troubleshoot ? '▼' : '▶'}</span>
            </div>

            {expandedSections.troubleshoot && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Le guide de dépannage</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="troubleshoot" />
                </div>

                <p>ChatGPT n'est pas parfait. Voici comment réagir quand ça ne marche pas comme prévu.</p>

                <div className="troubleshoot-scenarios">
                  <div className="trouble-card">
                    <div className="trouble-problem">
                      <span className="trouble-icon">😕</span>
                      <h4>La réponse est trop vague/générique</h4>
                    </div>
                    <div className="trouble-solution">
                      <p><strong>Cause :</strong> Votre prompt manque de contexte ou de spécificité.</p>
                      <div className="trouble-fix">
                        <strong>Solutions :</strong>
                        <ul>
                          <li>"Sois plus spécifique sur [aspect]"</li>
                          <li>"Donne-moi des exemples concrets"</li>
                          <li>"Détaille davantage la partie sur [sujet]"</li>
                          <li>Ajoutez plus de contexte à votre demande initiale</li>
                        </ul>
                      </div>
                      <PromptBox prompt={`Ta réponse est trop générale. Je veux des conseils SPÉCIFIQUES pour [mon cas précis].

Contexte supplémentaire : [détails]

Donne-moi 5 actions concrètes et actionables.`} title="Prompt de clarification" />
                    </div>
                  </div>

                  <div className="trouble-card">
                    <div className="trouble-problem">
                      <span className="trouble-icon">🚫</span>
                      <h4>ChatGPT refuse de répondre</h4>
                    </div>
                    <div className="trouble-solution">
                      <p><strong>Cause :</strong> Votre demande touche à un sujet sensible ou est mal formulée.</p>
                      <div className="trouble-fix">
                        <strong>Solutions :</strong>
                        <ul>
                          <li>Reformulez de façon plus neutre</li>
                          <li>Précisez le contexte légitime (recherche, fiction, éducation)</li>
                          <li>Décomposez en questions plus petites</li>
                          <li>Demandez une perspective académique/analytique</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="trouble-card">
                    <div className="trouble-problem">
                      <span className="trouble-icon">📏</span>
                      <h4>La réponse est trop longue/courte</h4>
                    </div>
                    <div className="trouble-solution">
                      <p><strong>Cause :</strong> Vous n'avez pas spécifié la longueur souhaitée.</p>
                      <div className="trouble-fix">
                        <strong>Solutions :</strong>
                        <ul>
                          <li>"Résume en 3 bullet points"</li>
                          <li>"Développe en 500 mots minimum"</li>
                          <li>"Fais une version courte (2 phrases) et une longue (2 paragraphes)"</li>
                          <li>"Trop long, condense à l'essentiel"</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="trouble-card">
                    <div className="trouble-problem">
                      <span className="trouble-icon">🎭</span>
                      <h4>Le ton n'est pas bon</h4>
                    </div>
                    <div className="trouble-solution">
                      <p><strong>Cause :</strong> Vous n'avez pas précisé le style souhaité.</p>
                      <div className="trouble-fix">
                        <strong>Solutions :</strong>
                        <ul>
                          <li>"Réécris de façon plus [décontractée/formelle/technique/simple]"</li>
                          <li>"Adapte pour un public [expert/débutant/enfant]"</li>
                          <li>"Moins corporate, plus humain"</li>
                          <li>"Comme si tu parlais à un ami"</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="trouble-card">
                    <div className="trouble-problem">
                      <span className="trouble-icon">🔄</span>
                      <h4>ChatGPT répète toujours la même chose</h4>
                    </div>
                    <div className="trouble-solution">
                      <p><strong>Cause :</strong> Il est "bloqué" dans un pattern.</p>
                      <div className="trouble-fix">
                        <strong>Solutions :</strong>
                        <ul>
                          <li>Commencez une NOUVELLE conversation</li>
                          <li>"Propose une approche complètement différente"</li>
                          <li>"Oublie ta réponse précédente et repars de zéro"</li>
                          <li>Changez radicalement l'angle de votre question</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="trouble-card">
                    <div className="trouble-problem">
                      <span className="trouble-icon">❌</span>
                      <h4>Les informations sont fausses</h4>
                    </div>
                    <div className="trouble-solution">
                      <p><strong>Cause :</strong> Hallucination ou connaissances obsolètes.</p>
                      <div className="trouble-fix">
                        <strong>Solutions :</strong>
                        <ul>
                          <li>Activez la navigation web pour les infos récentes</li>
                          <li>"Cite tes sources pour que je puisse vérifier"</li>
                          <li>"Es-tu sûr de cette information ? Vérifie."</li>
                          <li>Vérifiez TOUJOURS les faits importants vous-même</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="iteration-guide">
                  <h4>🔄 L'art de l'itération : améliorer progressivement</h4>
                  <p>Ne vous attendez pas à la perfection du premier coup. Voici le workflow :</p>
                  <div className="iteration-steps">
                    <div className="iteration-step">
                      <span className="iter-num">1</span>
                      <div>
                        <strong>Premier prompt</strong>
                        <p>Posez votre question/demande initiale</p>
                      </div>
                    </div>
                    <div className="iteration-step">
                      <span className="iter-num">2</span>
                      <div>
                        <strong>Évaluez la réponse</strong>
                        <p>Qu'est-ce qui est bien ? Qu'est-ce qui manque ?</p>
                      </div>
                    </div>
                    <div className="iteration-step">
                      <span className="iter-num">3</span>
                      <div>
                        <strong>Affinez</strong>
                        <p>"C'est bien, mais [modification souhaitée]"</p>
                      </div>
                    </div>
                    <div className="iteration-step">
                      <span className="iter-num">4</span>
                      <div>
                        <strong>Répétez</strong>
                        <p>2-4 itérations suffisent généralement</p>
                      </div>
                    </div>
                  </div>
                </div>

                <PromptBox prompt={`C'est un bon début, mais :
- [Ce qui manque]
- [Ce qu'il faut changer]
- [Le ton à ajuster]

Réécris en prenant en compte ces retours.`} title="Template d'itération" />
              </div>
            )}
          </div>

          {/* ==================== SECTION 14: FAQ ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('faq')}>
              <div className="section-toggle-content">
                <span className="section-number">14</span>
                <div>
                  <h2>FAQ - Questions fréquentes</h2>
                  <p>Les vraies questions que tout le monde se pose</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.faq ? '▼' : '▶'}</span>
            </div>

            {expandedSections.faq && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Toutes vos questions, nos réponses</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="faq" />
                </div>

                <div className="faq-list">
                  <div className="faq-item">
                    <div className="faq-q">❓ Combien de temps pour être à l'aise avec ChatGPT ?</div>
                    <div className="faq-a">
                      <strong>1 heure</strong> pour les bases (poser des questions, obtenir des réponses).
                      <strong>1 semaine</strong> d'usage quotidien pour être efficace.
                      <strong>1 mois</strong> pour maîtriser les techniques avancées.
                      La clé : pratiquez tous les jours, même 10 minutes.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ ChatGPT gratuit est-il suffisant ?</div>
                    <div className="faq-a">
                      <strong>Pour débuter : OUI.</strong> GPT-3.5 gratuit couvre 80% des besoins.
                      <strong>Passez à Plus si :</strong> vous l'utilisez intensivement, avez besoin d'images (DALL-E), de navigation web, ou de réponses plus précises.
                      Notre conseil : commencez gratuit, upgradez quand vous sentez les limites.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ ChatGPT garde-t-il mes conversations ?</div>
                    <div className="faq-a">
                      <strong>Oui</strong>, par défaut vos conversations sont sauvegardées dans votre historique.
                      <strong>Vous pouvez :</strong> les supprimer manuellement, désactiver l'historique dans les paramètres, ou utiliser le mode "conversation temporaire".
                      <strong>Note :</strong> désactiver l'historique empêche aussi OpenAI d'utiliser vos chats pour l'entraînement.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Puis-je utiliser ChatGPT pour mon travail sans le dire ?</div>
                    <div className="faq-a">
                      <strong>Techniquement oui</strong>, mais vérifiez la politique de votre entreprise.
                      <strong>Attention :</strong> ne partagez jamais de données confidentielles de l'entreprise.
                      <strong>Conseil :</strong> beaucoup d'entreprises encouragent maintenant l'usage de l'IA. Demandez ou proposez une politique claire.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ ChatGPT parle-t-il bien français ?</div>
                    <div className="faq-a">
                      <strong>Très bien !</strong> Le français est l'une des langues les mieux supportées. Grammaire, expressions, ton formel/informel — tout y est. Pour des textes critiques, une relecture humaine reste recommandée.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Puis-je utiliser ChatGPT hors connexion ?</div>
                    <div className="faq-a">
                      <strong>Non.</strong> ChatGPT nécessite une connexion internet. L'IA tourne sur les serveurs d'OpenAI, pas sur votre appareil. Pour du hors-ligne, il existe des alternatives locales (LLama, Mistral...) mais elles sont plus techniques.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ ChatGPT peut-il écrire comme moi ?</div>
                    <div className="faq-a">
                      <strong>Oui, avec entraînement.</strong> Donnez-lui des exemples de votre style d'écriture et demandez-lui de s'en inspirer. Plus vous itérez, mieux il vous imite. Vous pouvez aussi créer un GPT personnalisé avec votre style.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Comment ChatGPT gagne-t-il de l'argent ?</div>
                    <div className="faq-a">
                      <strong>Abonnements Plus/Team/Enterprise</strong> (20-60$/mois par utilisateur) et <strong>API pour développeurs</strong> (facturation à l'usage). La version gratuite sert à attirer les utilisateurs et améliorer le modèle.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Et si ChatGPT disparaît demain ?</div>
                    <div className="faq-a">
                      <strong>Peu probable.</strong> OpenAI est valorisé à 100+ milliards $, soutenu par Microsoft. Mais même si : les compétences en prompting sont transférables. Claude, Gemini, etc. fonctionnent de façon similaire.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Comment rester à jour avec les nouveautés ?</div>
                    <div className="faq-a">
                      <strong>Sources recommandées :</strong>
                      <ul>
                        <li>Blog officiel OpenAI (openai.com/blog)</li>
                        <li>Twitter/X : @OpenAI, @sama (Sam Altman)</li>
                        <li>YouTube : cherchez "ChatGPT news" ou "AI updates"</li>
                        <li>Notre communauté Telegram F12IA™</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="final-encouragement">
                  <h4>🚀 Vous êtes prêt(e) !</h4>
                  <p>
                    Vous avez maintenant toutes les connaissances pour utiliser ChatGPT efficacement.
                    La seule chose qui manque ? <strong>La pratique.</strong>
                  </p>
                  <p>
                    Commencez dès aujourd'hui. Posez une question. Rédigez un email. Demandez une idée.
                    Plus vous pratiquez, plus vous devenez expert(e).
                  </p>
                  <p className="encouragement-cta">
                    Dans 30 jours, vous vous demanderez comment vous faisiez avant. 💪
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ==================== QUIZ ==================== */}
          <section className="module-section">
            <h2>📝 Quiz de validation</h2>
            <p>Testez vos connaissances sur ChatGPT. Score minimum : 70%</p>
            <Quiz moduleId={MODULE_ID} questions={quizQuestions} />
          </section>

          {/* ==================== RÉSUMÉ ==================== */}
          <section className="module-section">
            <h2>🎓 Ce que vous savez maintenant</h2>
            <div className="summary-box large">
              <div className="summary-grid">
                <div className="summary-item">
                  <span className="summary-check">✓</span>
                  <span>Ce qu'est ChatGPT et comment il fonctionne</span>
                </div>
                <div className="summary-item">
                  <span className="summary-check">✓</span>
                  <span>Les différences entre GPT-3.5, GPT-4 et GPT-4o</span>
                </div>
                <div className="summary-item">
                  <span className="summary-check">✓</span>
                  <span>Comment accéder et configurer ChatGPT</span>
                </div>
                <div className="summary-item">
                  <span className="summary-check">✓</span>
                  <span>La méthode CRISP pour des prompts parfaits</span>
                </div>
                <div className="summary-item">
                  <span className="summary-check">✓</span>
                  <span>100+ cas d'usage concrets</span>
                </div>
                <div className="summary-item">
                  <span className="summary-check">✓</span>
                  <span>Les techniques avancées (chain of thought, few-shot...)</span>
                </div>
                <div className="summary-item">
                  <span className="summary-check">✓</span>
                  <span>Comment l'utiliser au travail efficacement</span>
                </div>
                <div className="summary-item">
                  <span className="summary-check">✓</span>
                  <span>Les limites et bonnes pratiques</span>
                </div>
              </div>
            </div>
          </section>

          {/* Telegram */}
          <TelegramWidget />

          {/* Navigation */}
          <div className="module-navigation">
            <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>← Dashboard</button>
            <button className="btn btn-primary" onClick={() => navigate('/formation/claude')}>Parcours Claude Code →</button>
          </div>

        </div>
      </main>
    </div>
  );
};

export default ChatGPTTrack;
